import { useState, useEffect } from "react";
import ProductCard from "./ProductCard";
import { getNewArrivalProducts } from "../services/productService";

const IMG_BASE =
  import.meta.env.VITE_IMG_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const price = 20 + (p.name.charCodeAt(0) % 50);
  const oldPrice = price + 10;
  const primary = p.images?.find((img) => img.is_primary) || p.images?.[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = p.images?.length > 1 ? p.images[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category?.name || "",
    category_id: p.category_id,
    price,
    oldPrice,
    img1,
    img2,
    description: p.description,
    additional_information: p.additional_information,
    status: p.status,
    images: p.images || [],
  };
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

export default function NewArrivals() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const fetchProducts = async () => {
      try {
        const res = await getNewArrivalProducts();
        const data = res.data?.data || res.data || [];
        if (isMounted) {
          setProducts(data.map(mapApiProduct));
        }
      } catch {
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, []);

  // Each page shows 6 products in the New Arrivals grid
  const newArrivalPages = chunkArray(products, 6);

  // Top Rated uses first 4 products (static, no pagination)
  const topRatedProducts = products.slice(0, 4);

  if (loading) {
    return (
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* New Arrivals Skeleton */}
            <div className="lg:col-span-2">
              <div className="section-title mb-6">
                <p>Recently added</p>
                <h2>New Arrivals</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="border border-border-color bg-white h-[320px] p-3 animate-pulse flex flex-col justify-between">
                    <div className="w-full h-56 bg-gray-200 rounded mb-3"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-1/3 mx-auto"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Rated Skeleton */}
            <div>
              <div className="section-title mb-6">
                <p>Our favorites</p>
                <h2>Top Rated</h2>
              </div>
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex gap-3 items-center border-b border-border-color pb-4 animate-pulse">
                    <div className="w-20 h-20 bg-gray-200 flex-shrink-0 rounded"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* New Arrivals */}
          <div className="lg:col-span-2">
            <div className="section-title mb-6">
              <p>Recently added</p>
              <h2>New Arrivals</h2>
            </div>

            <div className="relative flex items-center">
              {/* Left Arrow */}
              <button
                onClick={() => setPage((i) => Math.max(0, i - 1))}
                disabled={page === 0}
                className="absolute -left-2 md:-left-6 z-10 w-8 h-8 rounded-full bg-white/90 md:bg-transparent shadow-sm md:shadow-none flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
                aria-label="Previous"
              >
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path
                    d="M9 1L1 9L9 17"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className="overflow-hidden w-full">
                {/* <div className="grid grid-cols-2 sm:grid-cols-3 gap-4"> */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 [&>*:nth-child(n+5)]:hidden sm:[&>*:nth-child(n+5)]:block">
                  {(newArrivalPages[page] || []).map((p) => (
                    <ProductCard key={p.product_id} product={p} />
                  ))}
                </div>
              </div>

              {/* Right Arrow */}
              <button
                onClick={() =>
                  setPage((i) => Math.min(newArrivalPages.length - 1, i + 1))
                }
                disabled={page >= newArrivalPages.length - 1}
                className="absolute -right-2 md:-right-6 z-10 w-8 h-8 rounded-full bg-white/90 md:bg-transparent shadow-sm md:shadow-none flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
                aria-label="Next"
              >
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path
                    d="M1 1L9 9L1 17"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Top Rated */}
          <div>
            <div className="section-title mb-6">
              <p>Our favorites</p>
              <h2>Top Rated</h2>
            </div>
            <div className="space-y-4">
              {topRatedProducts.map((p) => (
                <div
                  key={p.product_id}
                  className="flex gap-3 items-center border-b border-border-color pb-4"
                >
                  <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-gray-50">
                    <img
                      src={p.img1}
                      alt={p.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-dark hover:text-primary cursor-pointer">
                      {p.name}
                    </h4>
                    <p className="text-xs text-gray-400 mt-0.5">{p.category}</p>
                    <div className="mt-1 flex gap-2 items-center">
                      {/* <span className="text-primary text-sm font-bold">
                        ₹{p.price}.00
                      </span>
                      <span className="text-gray-400 line-through text-xs">
                        ₹{p.oldPrice}.00
                      </span> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
